import React from 'react';
import { ModernCard, ModernBadge } from './ModernComponents';

// CSS-based mobile detection hook
const useMobileDetection = (breakpoint = 'md') => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkScreenSize = () => {
      const breakpoints = {
        sm: 640,
        md: 768,
        lg: 1024
      };
      const currentWidth = window.innerWidth;
      const breakpointValue = breakpoints[breakpoint];
      const mobile = currentWidth < breakpointValue;
      setIsMobile(mobile);
      console.log(`Mobile Detection - Screen: ${currentWidth}px, Breakpoint: ${breakpointValue}px, Is Mobile: ${mobile}`);
    };

    // Check immediately
    checkScreenSize();
    
    // Add event listener
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [breakpoint]);

  return isMobile;
};

// Responsive Table Component that automatically switches between table and card view
export const ResponsiveTable = ({ 
  columns = [], 
  data = [], 
  className = '', 
  cardViewBreakpoint = 'md', // 'sm', 'md', 'lg'
  ...props 
}) => {
  const isMobile = useMobileDetection(cardViewBreakpoint);

  // Mobile Card View - Automatic on mobile
  const renderCardView = () => {
    console.log('ResponsiveTable - Rendering card view for', data.length, 'items');
    return (
      <div className="space-y-4">
        {data.map((row, index) => (
          <ModernCard key={index} className="p-4 hover:shadow-md transition-shadow">
            <div className="space-y-3">
              {columns.map((column) => {
                if (column.key === 'actions') return null; // Handle actions separately
                
                return (
                  <div key={column.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center mb-2 sm:mb-0">
                      <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                        {column.label}:
                      </span>
                    </div>
                    <div className="flex-1">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </div>
                  </div>
                );
              })}
              
              {/* Actions at the bottom */}
              {columns.find(col => col.key === 'actions') && (
                <div className="pt-3 border-t border-neutral-200">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    {columns.find(col => col.key === 'actions')?.render?.(null, row)}
                  </div>
                </div>
              )}
            </div>
          </ModernCard>
        ))}
      </div>
    );
  };

  // Desktop Table View - Automatic on desktop
  const renderTableView = () => {
    console.log('ResponsiveTable - Rendering table view for', data.length, 'items');
    return (
      <div className="overflow-x-auto">
        <table className={`table ${className}`} {...props}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="whitespace-nowrap">{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.key} className="whitespace-nowrap">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Automatically render cards on mobile, table on desktop
  console.log('ResponsiveTable - Current state - isMobile:', isMobile, 'Data length:', data.length);
  return isMobile ? renderCardView() : renderTableView();
};

// Enhanced Card View for complex data
export const ResponsiveCardList = ({ 
  data = [], 
  renderCard, 
  className = '',
  ...props 
}) => {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`} {...props}>
      {data.map((item, index) => (
        <div key={index}>
          {renderCard(item, index)}
        </div>
      ))}
    </div>
  );
};

// Compact Card View for simple lists
export const ResponsiveCompactList = ({ 
  data = [], 
  columns = [], 
  className = '',
  ...props 
}) => {
  return (
    <div className={`space-y-3 ${className}`} {...props}>
      {data.map((row, index) => (
        <ModernCard key={index} className="p-3 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {columns.slice(0, 2).map((column) => (
                <div key={column.key} className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-neutral-500">
                    {column.label}:
                  </span>
                  <span className="text-sm font-medium text-neutral-900 truncate">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Actions */}
            {columns.find(col => col.key === 'actions') && (
              <div className="flex items-center space-x-2 ml-4">
                {columns.find(col => col.key === 'actions')?.render?.(null, row)}
              </div>
            )}
          </div>
        </ModernCard>
      ))}
    </div>
  );
};

// Automatic Responsive Table - No toggle needed, fully automatic
export const ResponsiveHybridTable = ({ 
  columns = [], 
  data = [], 
  className = '',
  cardViewBreakpoint = 'md', // 'sm', 'md', 'lg'
  ...props 
}) => {
  const isMobile = useMobileDetection(cardViewBreakpoint);

  // Automatically render cards on mobile, table on desktop - NO TOGGLE
  console.log('ResponsiveHybridTable - Current state - isMobile:', isMobile, 'Data length:', data.length);
  
  if (isMobile) {
    // Mobile Card View
    console.log('ResponsiveHybridTable - Rendering mobile cards');
    return (
      <div className="space-y-4">
        {data.map((row, index) => (
          <ModernCard key={index} className="p-4 hover:shadow-md transition-shadow">
            <div className="space-y-3">
              {columns.map((column) => {
                if (column.key === 'actions') return null; // Handle actions separately
                
                return (
                  <div key={column.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center mb-2 sm:mb-0">
                      <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                        {column.label}:
                      </span>
                    </div>
                    <div className="flex-1">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </div>
                  </div>
                );
              })}
              
              {/* Actions at the bottom */}
              {columns.find(col => col.key === 'actions') && (
                <div className="pt-3 border-t border-neutral-200">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    {columns.find(col => col.key === 'actions')?.render?.(null, row)}
                  </div>
                </div>
              )}
            </div>
          </ModernCard>
        ))}
      </div>
    );
  } else {
    // Desktop Table View
    console.log('ResponsiveHybridTable - Rendering desktop table');
    return (
      <div className="overflow-x-auto">
        <table className={`table ${className}`} {...props}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="whitespace-nowrap">{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.key} className="whitespace-nowrap">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
};

// Legacy Hybrid Table with Toggle (for backward compatibility)
export const ResponsiveHybridTableWithToggle = ({ 
  columns = [], 
  data = [], 
  className = '',
  defaultView = 'auto', // 'auto', 'table', 'cards'
  showToggle = true,
  ...props 
}) => {
  const [viewMode, setViewMode] = React.useState(defaultView);
  const isMobile = useMobileDetection('md');

  React.useEffect(() => {
    if (defaultView === 'auto') {
      setViewMode(isMobile ? 'cards' : 'table');
    }
  }, [isMobile, defaultView]);

  const shouldShowCards = viewMode === 'cards' || (viewMode === 'auto' && isMobile);

  return (
    <div>
      {showToggle && (
        <div className="flex justify-end mb-4">
          <div className="flex items-center space-x-2 bg-neutral-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table' 
                  ? 'bg-white text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards' 
                  ? 'bg-white text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Cards
            </button>
          </div>
        </div>
      )}

      {shouldShowCards ? (
        <ResponsiveTable 
          columns={columns} 
          data={data} 
          className={className}
          cardViewBreakpoint="lg"
          {...props}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className={`table ${className}`} {...props}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className="whitespace-nowrap">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td key={column.key} className="whitespace-nowrap">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default {
  ResponsiveTable,
  ResponsiveCardList,
  ResponsiveCompactList,
  ResponsiveHybridTable,
  ResponsiveHybridTableWithToggle
};
